import { AgGridCellTooltipComponent, AgGridCellTooltipValueGetter } from 'components/AgGrid/AgGridCellTooltipComponent';
import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { useRequest } from 'features/request/useRequest';
import type { ContragentTypeEnum, MtrTypeEnum } from 'gql/graphql';
import { SortEnumType } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import { getTagLabelDefault } from 'uikit/getTagLabelDefault';
import { formatNumberToRu, getCheckedIdsArr } from 'utils';

import type { TextFieldPropValue } from '@consta/uikit/TextField';
import { debounce } from 'debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { labelContragentType, labelMtrType } from '../../MatchMtrDialog/hooks/useUerMtrRowsForComparisonColumns';
import { CellRenderCheckbox } from '../../WorkOrder/AddMtrDialog/cellRender/CellRenderCheckbox';
import { CellRenderTextField } from '../../WorkOrder/AddMtrDialog/cellRender/CellRenderTextField';
import { getWhere } from '../../WorkOrder/AddMtrDialog/helpers';
import { getWorkOrderChangeMtrPriceList } from '../WorkOrderChangeQueries';

export const useAddMtrToWorkOrderTableData = ({
    workOrderChangePriceId,
    workOrderPriceId,
    locationState,
}: {
    workOrderChangePriceId: number | null;
    workOrderPriceId: number;
    locationState: { selected: number; reload: number } | undefined;
}) => {
    const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});
    const [valueNote, setValueNote] = useState<Record<string, string>>({});
    const [searchString, setSearchString] = useState('');

    const filterSaveKey = useMemo(
        () => `addMtrToWoCh${workOrderPriceId || workOrderChangePriceId}`,
        [workOrderPriceId, workOrderChangePriceId],
    );

    const { savedFilterState, handleSaveFilterState } = usePageFilterSaver({
        withFakeSetter: true,
        customSaveKey: filterSaveKey,
    });

    const variables = useMemo(
        () => ({
            take: 1000,
            workOrderChangePriceId,
            workOrderPriceId,
            where: getWhere(savedFilterState || {}),
            order: [{ code: SortEnumType.Asc }],
        }),
        [savedFilterState, workOrderChangePriceId, workOrderPriceId],
    );

    const [mtrToWorkOrderChangeData, mtrToWorkOrderChangeStatus, , makeRequestMtrChangeData] = useRequest({
        requestId: 'workOrderChangeMtrPriceList',
        query: getWorkOrderChangeMtrPriceList,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'workOrderChangeMtrPriceList',
        requestFn: makeRequestMtrChangeData,
        requestVariables: variables,
    });

    useEffect(() => {
        if (mtrToWorkOrderChangeData?.uerWorkOrderChangePriceListMtrQuery?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                mtrToWorkOrderChangeData.uerWorkOrderChangePriceListMtrQuery.items,
                mtrToWorkOrderChangeData.uerWorkOrderChangePriceListMtrQuery.totalCount,
            );
        }
    }, [
        getRowParams,
        mtrToWorkOrderChangeData?.uerWorkOrderChangePriceListMtrQuery?.items,
        mtrToWorkOrderChangeData?.uerWorkOrderChangePriceListMtrQuery?.totalCount,
    ]);

    const changeCheckboxHandler = useCallback(
        (rowId: number) =>
            ({ checked }: { checked: boolean }) => {
                setCheckedIds((ids) => ({ ...ids, [rowId]: checked }));
            },
        [],
    );

    useEffect(() => {
        if (locationState?.selected) {
            setCheckedIds((ids) => ({ ...ids, [locationState?.selected]: true }));
        }
    }, [changeCheckboxHandler, locationState?.selected]);

    const handleChangesValueNote = useCallback(
        (rowId: number) =>
            ({ value }: { value: TextFieldPropValue }) => {
                setValueNote((ids) => ({ ...ids, [rowId]: value }));
            },
        [],
    );

    const changeFilterHandler = useCallback<(params: { fieldName: string; checkedValues: string[] }) => void>(
        ({ fieldName, checkedValues }) => {
            handleSaveFilterState({
                key: fieldName,
                newState: checkedValues,
            });
        },
        [handleSaveFilterState],
    );

    const columns = useMemo(
        () => [
            {
                headerName: 'Шифр',
                field: 'code',
                width: 160,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    dataAccessor: 'uerWorkOrderChangePriceListMtrQuery',
                    query: getWorkOrderChangeMtrPriceList,
                    variables: {
                        ...variables,
                    },
                    whereVariables: ['code'],
                    childFieldAccessor: 'code',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'code',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Наименование',
                field: 'name',
                width: 320,
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    dataAccessor: 'uerWorkOrderChangePriceListMtrQuery',
                    query: getWorkOrderChangeMtrPriceList,
                    variables: {
                        ...variables,
                    },
                    whereVariables: ['name'],
                    childFieldAccessor: 'name',
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'name',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Ед. изм',
                field: 'unit',
                width: 88,
            },
            {
                headerName: 'Стоимость единицы, руб. без ндс',
                field: 'unitCost',
                cellStyle: { textAlign: 'right' },
                width: 200,
                valueFormatter: (row: any) => formatNumberToRu(row?.unitCost, 2),
            },
            {
                headerName: 'Поставка',
                field: 'supplyBy',
                width: 120,
                valueFormatter: ({ data }: { data?: { supplyBy?: ContragentTypeEnum } }) =>
                    data?.supplyBy ? labelContragentType[data?.supplyBy] : '',
            },
            {
                headerName: 'Тип',
                field: 'mtrType',
                width: 100,
                valueFormatter: ({ data }: { data?: { mtrType?: MtrTypeEnum } }) =>
                    data?.mtrType ? labelMtrType[data.mtrType] : '',
            },
        ],
        [changeFilterHandler, variables],
    );

    const finalColumns = useMemo(
        () => [
            {
                headerName: '',
                field: 'checkbox',
                width: 60,
                flex: 0,
                cellClass: 'firstColumn',
                spanHeaderHeight: true,
                cellRenderer: CellRenderCheckbox,
                cellRendererParams: {
                    checkedIds,
                    handleChangeCheckbox: changeCheckboxHandler,
                },
            },
            ...columns,
            {
                headerName: 'Примечание',
                field: 'note',
                width: 200,
                cellRenderer: CellRenderTextField,
                cellRendererParams: {
                    checkedIds,
                    handleChangesValueNote,
                    valueNote,
                },
            },
        ],
        [changeCheckboxHandler, checkedIds, columns, handleChangesValueNote, valueNote],
    );

    const checkedMtrArr = useMemo(() => getCheckedIdsArr(checkedIds), [checkedIds]);

    const searchHandler = useCallback(
        ({ value }) => {
            handleSaveFilterState({
                key: 'searchString',
                newState: value,
            });
        },
        [handleSaveFilterState],
    );

    const debouncedSearch = useMemo(() => debounce(searchHandler, 500), [searchHandler]);

    const changeSearchStringHandler = useCallback(
        ({ value }) => {
            setSearchString(value);
            debouncedSearch({ value });
        },
        [debouncedSearch],
    );

    const clearSearchStringHandler = useCallback(() => {
        if (searchString) {
            setSearchString('');

            handleSaveFilterState({
                key: 'searchString',
                newState: '',
            });
        }
    }, [handleSaveFilterState, searchString]);

    const getTagLabel = useCallback(
        (id: string, name: string, filterValue: any) => getTagLabelDefault(id, name, filterValue),
        [],
    );

    const mtrInputs = useMemo(
        () =>
            checkedMtrArr.map((mtrRowId) => ({
                rationale: valueNote[mtrRowId] || '',
                mtrRowId,
            })),
        [checkedMtrArr, valueNote],
    );

    const clearFilters = useCallback(() => {
        columns
            .filter((c) => !!c.filterParams)
            .forEach((c) => {
                handleSaveFilterState({
                    key: c.field,
                    newState: undefined,
                });
            });

        handleSaveFilterState({
            key: 'searchString',
            newState: '',
        });
    }, [columns, handleSaveFilterState]);

    const debouncedSearchValue = useMemo(() => savedFilterState?.searchString, [savedFilterState?.searchString]);

    return {
        columns: finalColumns,
        getMtrStatus: mtrToWorkOrderChangeStatus,
        checkedMtrArr,
        searchString,
        changeSearchStringHandler,
        searchHandler,
        clearSearchStringHandler,
        getTagLabel,
        mtrInputs,
        datasource: dataSource,
        clearFilters,
        debouncedSearchValue,
    };
};
